import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { RequiredText } from '../../common/components';
import {
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectView,
  IndicatorFormDataType,
} from '../types';
import {
  CloneFormTableRowButton,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  filterOption,
  FormCheckbox,
  FormField,
  FormInput,
  FormSelect,
  FormTableSequenceNumberField,
  getFormTableFieldName,
  TanstackFormTable,
} from '../../common/form';
import { useWatch } from 'react-hook-form';
import { FC, memo, useCallback, useMemo } from 'react';
import translations from '../translations/DataObjectIndicatorTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../../configuration';
import { FormInputNumber } from '../../common/form';
import { Flex } from 'antd';
import styled from 'styled-components';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-object-indicator-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<IndicatorFormDataType>();

export function DataObjectIndicatorTable({
  dataObjectView,
  defaultStatusCode,
}: {
  dataObjectView: DataObjectView;
  defaultStatusCode: string;
}) {
  const { t } = useTranslation();
  const [outputTypeCode] = useWatch<DataObjectFormDataType, ['outputTypeCode']>(
    {
      name: ['outputTypeCode'],
    },
  );
  const isDraft = defaultStatusCode === 'DRAFT';

  const isDeletable = useCallback(
    (rowData: IndicatorFormDataType) => {
      return rowData.code !== 'SUBJEKT' && isDraft;
    },
    [isDraft],
  );
  const isEditableDraft = useCallback(
    (indicator: IndicatorFormDataType) => isDraft || !indicator.id,
    [isDraft],
  );

  const {
    INDICATOR_UNIT: indicatorUnits,
    MULTIPLIER: multipliers,
    PERIOD_DATA_TYPE: periodDataTypes,
    DEFAULT_INDICATOR_DIMENSION_WIDTH: [defaultWidth],
  } = useSystemClassifierSelectOptions([
    'PERIOD_DATA_TYPE',
    'MULTIPLIER',
    'INDICATOR_UNIT',
    'DEFAULT_INDICATOR_DIMENSION_WIDTH',
  ]);
  const defaultIndicatorFormData: IndicatorFormDataType = useMemo(() => {
    const multiplierId =
      multipliers.find((item) => item.code === 'ONE')?.value || undefined;
    const unitId =
      indicatorUnits?.find((item) => item.code === 'EUR')?.value || undefined;
    return {
      id: undefined,
      code: '',
      name: '',
      description: '',
      width: Number(defaultWidth.code),
      sequenceNumber: 0,
      periodTypeId: undefined,
      multiplierId,
      unitId,
      required: false,
    } as IndicatorFormDataType;
  }, [defaultWidth.code, indicatorUnits, multipliers]);

  const cols = useMemo(() => {
    const cols = [
      columnHelper.accessor('sequenceNumber', {
        header: t('field.sequenceNumber.label'),
        cell: FormTableSequenceNumberField,
        meta: {
          width: 110,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('code', {
        header: () => <RequiredText>{t('field.code.label')}</RequiredText>,
        cell: (props) => (
          <DefaultCell {...props} isEditableDraft={isEditableDraft} />
        ),
        meta: {
          width: '30ch',
        },
      }),
      columnHelper.accessor('name', {
        header: () => <RequiredText>{t('field.name.label')}</RequiredText>,
        cell: (props) => <DefaultCell {...props} />,
        meta: {
          width: '50%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('description', {
        header: t('field.description.label'),
        cell: (props) => <DefaultCell {...props} />,
        meta: {
          width: '50%',
          minWidth: 150,
        },
      }),
    ] as ColumnDef<IndicatorFormDataType, any>[];

    if (dataObjectView === DataObjectView.OUT) {
      cols.push(
        columnHelper.accessor('decimals', {
          header: t('field.decimals.label'),
          cell: (props) => (
            <DecimalsCell {...props} isEditableDraft={isEditableDraft} />
          ),
          meta: {
            width: 135,
          },
        }),
      );
    }

    cols.push(
      columnHelper.accessor('periodTypeId', {
        header: () =>
          outputTypeCode !== DataObjectOutputType.REPORT ? (
            <RequiredText>{t('field.periodType.label')}</RequiredText>
          ) : (
            t('field.periodType.label')
          ),
        cell: (props) => (
          <SelectCell
            {...props}
            isEditableDraft={isEditableDraft}
            options={periodDataTypes}
          />
        ),
        meta: {
          width: 150,
        },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('multiplierId', {
        header: () => (
          <RequiredText>{t('field.multiplier.label')}</RequiredText>
        ),
        cell: (props) => (
          <SelectCell
            {...props}
            isEditableDraft={isEditableDraft}
            options={multipliers}
          />
        ),
        meta: {
          width: 120,
          textAlign: 'right',
        },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('unitId', {
        header: () => <RequiredText>{t('field.unit.label')}</RequiredText>,
        cell: (props) => (
          <SelectCell
            {...props}
            isEditableDraft={isEditableDraft}
            options={indicatorUnits}
          />
        ),
        meta: {
          width: 120,
        },
        filterFn: 'includesOption',
      }),
    );

    if (dataObjectView === DataObjectView.IN) {
      cols.push(
        columnHelper.accessor('required', {
          header: t('field.required.label'),
          cell: (props) => (
            <CheckboxCell {...props} isEditableDraft={isEditableDraft} />
          ),
          meta: {
            width: 140,
          },
          filterFn: 'includesOption',
        }),
      );

      cols.push(
        columnHelper.accessor('imputeFromIndicator', {
          header: () => t('field.imputeFromIndicator.label'),
          cell: (props) => (
            <ImputeFromIndicatorCell
              {...props}
              isEditableDraft={isEditableDraft}
            />
          ),
          meta: {
            width: 140,
          },
          filterFn: 'includesString',
        }),
      );
    }

    if (dataObjectView === DataObjectView.OUT) {
      cols.push(
        columnHelper.accessor('intermediate', {
          header: t('field.intermediate.label'),
          cell: (props) => (
            <CheckboxCell {...props} isEditableDraft={isEditableDraft} />
          ),
          meta: {
            width: 150,
          },
          filterFn: 'includesOption',
        }),
      );
    }

    cols.push(
      columnHelper.accessor('width', {
        header: () => <RequiredText>{t('field.width.label')}</RequiredText>,
        cell: WidthCell,
        meta: {
          width: 130,
          textAlign: 'right',
        },
      }),
    );

    cols.push(
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            {isDraft && (
              <CreateFormTableRowButton
                title={t('button.add-indicator')}
                defaultRowValues={defaultIndicatorFormData}
                autoFocusField="code"
              />
            )}
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <OperationsCell {...props} isDeletable={isDeletable} />
        ),
        meta: {
          width: 80,
          className: 'sticky-right',
        },
      }),
    );

    return cols;
  }, [
    dataObjectView,
    defaultIndicatorFormData,
    indicatorUnits,
    isDeletable,
    isDraft,
    isEditableDraft,
    multipliers,
    outputTypeCode,
    periodDataTypes,
    t,
  ]);

  const options = useMemo(
    () => ({
      intermediate: [
        { id: true, name: t('field.intermediate.yes') },
        { id: false, name: t('field.intermediate.no') },
      ],
      periodTypeId: periodDataTypes.map((option) => ({
        id: option.value,
        name: option.label,
      })),
      multiplierId: multipliers.map((option) => ({
        id: option.value,
        name: option.label,
      })),
      required: [
        { id: true, name: t('field.required.yes') },
        { id: false, name: t('field.required.no') },
      ],
      unitId: indicatorUnits.map((option) => ({
        id: option.value,
        name: option.label,
      })),
    }),
    [periodDataTypes, multipliers, indicatorUnits, t],
  );

  return (
    <TableWrapper vertical align="end" gap="middle">
      <TanstackFormTable
        name="indicators"
        columns={cols}
        options={options}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </TableWrapper>
  );
}

const DefaultCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isEditableDraft?: (indicator: IndicatorFormDataType) => boolean;
  }
> = memo((props) => {
  const disabled = !(props.isEditableDraft?.(props.row.original) ?? true);
  const name = getFormTableFieldName(props);
  return (
    <FormField name={name} size="small" preview disabled={disabled}>
      <FormInput name={name} disabled={disabled} size="small" autoFocus />
    </FormField>
  );
});

const DecimalsCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isEditableDraft: (indicator: IndicatorFormDataType) => boolean;
  }
> = memo((props) => {
  const disabled = !props.isEditableDraft(props.row.original);
  const name = getFormTableFieldName(props);
  return (
    <FormField size="small" name={name} preview disabled={disabled}>
      <FormInputNumber
        size="small"
        autoFocus
        name={name}
        disabled={disabled}
        decimalSeparator=","
      />
    </FormField>
  );
});

const ImputeFromIndicatorCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isEditableDraft: (indicator: IndicatorFormDataType) => boolean;
  }
> = memo((props) => {
  const [indicators] = useWatch<DataObjectFormDataType, ['indicators']>({
    name: ['indicators'],
  });
  const options = useMemo(
    () =>
      (indicators || [])
        .filter((indicator: IndicatorFormDataType) => indicator.code)
        .map((indicator: IndicatorFormDataType) => ({
          label: indicator.code + ' - ' + indicator.name,
          value: indicator.code,
        })),
    [indicators],
  );

  return <SelectCell {...props} options={options} />;
});

const SelectCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isEditableDraft: (indicator: IndicatorFormDataType) => boolean;
    options: Omit<SystemClassifierOption, 'code'>[];
  }
> = memo((props) => {
  const disabled = !props.isEditableDraft(props.row.original);
  const name = getFormTableFieldName(props);
  const columnId = props.cell.column.id;

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (val) => renderPreview(val, props.options),
      }}
      disabled={disabled}
    >
      <FormSelect
        name={name}
        size="small"
        autoFocus
        defaultOpen
        className={`${columnId}-select`}
        filterOption={filterOption}
        options={props.options}
        showSearch
        disabled={disabled}
        allowClear
      />
    </FormField>
  );
});

const CheckboxCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isEditableDraft: (indicator: IndicatorFormDataType) => boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);

  return (
    <Flex justify="center" align="center">
      <FormField size="small" name={name} preview={false}>
        <FormCheckbox
          disabled={!props.isEditableDraft(props.row.original)}
          name={name}
        />
      </FormField>
    </Flex>
  );
});

const WidthCell: FC<CellContext<IndicatorFormDataType, any>> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField size="small" name={name} preview>
      <FormInputNumber
        size="small"
        name={name}
        min="1"
        max="999"
        decimalSeparator=","
      />
    </FormField>
  );
});

const OperationsCell: FC<
  CellContext<IndicatorFormDataType, any> & {
    isDeletable: (indicator: IndicatorFormDataType) => boolean;
  }
> = memo((props) => {
  const disabled = !props.isDeletable(props.row.original);
  return (
    <Flex justify="center" gap="small">
      <CloneFormTableRowButton
        id={`clone-indicator-${props.row.original.code}`}
        context={props}
        resetId
        disabled={disabled}
        autoFocusField="code"
      />
      <DeleteFormTableRowButton
        id={`delete-indicator-${props.row.original.code}`}
        context={props}
        disabled={disabled}
      />
    </Flex>
  );
});

function renderPreview(
  value?: number,
  options?: Omit<SystemClassifierOption, 'code'>[],
) {
  return options?.find((item) => item?.value === value)?.label;
}

export const TableWrapper = styled(Flex)`
  margin: auto;
`;
